/**
 * /admin/shortcodes — the registry viewer + minter.
 *
 * Lists every active shortcode in the platform, lets you mint new ones,
 * and shows recent canonical-URL probe attempts (security alerting).
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const HANDLERS = [
  { key: "booking_widget", label: "Booking widget", version: "v1",
    fields: [
      { name: "venue_slug", label: "Venue slug", placeholder: "coffee-commune", required: true },
      { name: "accent_color", label: "Accent colour", placeholder: "#C75B3D", default: "#C75B3D" },
      { name: "title", label: "Title", placeholder: "Reserve a table" },
    ],
  },
];

export default function ShortcodesAdminPage() {
  const [items, setItems] = useState([]);
  const [probes, setProbes] = useState(null);
  const [showMint, setShowMint] = useState(false);
  const [handler, setHandler] = useState(HANDLERS[0].key);
  const [meta, setMeta] = useState({});
  const [clientId, setClientId] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [{ data: list }, { data: probeData }] = await Promise.all([
      axios.get(`${API}/api/admin/shortcodes`),
      axios.get(`${API}/api/admin/shortcodes/probes`),
    ]);
    setItems(list.items || []);
    setProbes(probeData);
  };
  useEffect(() => { load(); }, []);

  const handlerDef = HANDLERS.find((h) => h.key === handler);

  const mint = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/admin/shortcodes`, {
        handler_key: handler,
        version:     handlerDef.version,
        kind:        "snippet",
        metadata:    meta,
        client_id:   clientId || null,
      });
      setShowMint(false); setMeta({}); setClientId("");
      await load();
    } finally { setBusy(false); }
  };

  const retire = async (sc) => {
    if (!window.confirm(`Retire shortcode ${sc}? Existing embeds will stop rendering.`)) return;
    await axios.delete(`${API}/api/admin/shortcodes/${sc}`);
    await load();
  };

  const copy = (sc) => {
    const snippet = `[hustleplate sc="${sc}"]`;
    navigator.clipboard.writeText(snippet);
  };

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "32px 24px 80px", fontFamily: "Inter, sans-serif", color: "#1a1408" }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", paddingBottom: 18, marginBottom: 22, borderBottom: "1px solid #e6dfd1" }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: 4, textTransform: "uppercase", color: "#a64423", fontWeight: 700, marginBottom: 4 }}>
            🔐 Shortcode registry · public surface
          </div>
          <h1 style={{ fontFamily: "Georgia, serif", fontSize: 30, margin: 0 }}>Shortcodes</h1>
          <p style={{ color: "#888", fontSize: 13, margin: "4px 0 0" }}>
            Every public URL routes through here. Canonical handlers are unreachable from the internet.
          </p>
        </div>
        <button
          onClick={() => setShowMint((v) => !v)}
          style={{ padding: "10px 22px", background: "#1a1408", color: "#f3c84b", border: "none", borderRadius: 999, fontWeight: 700, letterSpacing: 1.4, textTransform: "uppercase", fontSize: 12, cursor: "pointer" }}
          data-testid="sc-mint-toggle"
        >
          + Mint shortcode
        </button>
      </header>

      {showMint && (
        <div style={{ background: "#fff", border: "1px solid #e6dfd1", borderRadius: 12, padding: 20, marginBottom: 22 }}>
          <h3 style={{ fontFamily: "Georgia, serif", margin: "0 0 14px" }}>Mint a new shortcode</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <label>
              <div style={lblStyle}>Handler</div>
              <select value={handler} onChange={(e) => { setHandler(e.target.value); setMeta({}); }} style={inputStyle}>
                {HANDLERS.map((h) => (<option key={h.key} value={h.key}>{h.label} ({h.version})</option>))}
              </select>
            </label>
            <label>
              <div style={lblStyle}>Client ID (optional · for per-WP-client scope)</div>
              <input value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="HP-XXXX-XXXX" style={inputStyle} />
            </label>
            {(handlerDef?.fields || []).map((f) => (
              <label key={f.name} style={{ gridColumn: f.name === "title" ? "1 / span 2" : "auto" }}>
                <div style={lblStyle}>{f.label}{f.required && " *"}</div>
                <input
                  value={meta[f.name] ?? f.default ?? ""}
                  onChange={(e) => setMeta({ ...meta, [f.name]: e.target.value })}
                  placeholder={f.placeholder}
                  style={inputStyle}
                />
              </label>
            ))}
          </div>
          <div style={{ marginTop: 18 }}>
            <button onClick={mint} disabled={busy} style={btnPrimary} data-testid="sc-mint-submit">
              {busy ? "Minting…" : "Mint + activate"}
            </button>
            <button onClick={() => setShowMint(false)} style={{ ...btnGhost, marginLeft: 8 }}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ background: "#fff", border: "1px solid #e6dfd1", borderRadius: 12, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr>
              <th style={th}>Shortcode</th>
              <th style={th}>Handler</th>
              <th style={th}>Metadata</th>
              <th style={th}>Client</th>
              <th style={th}>Rate / signed</th>
              <th style={th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.sc}>
                <td style={td}><code style={{ fontFamily: "ui-monospace,monospace", color: "#1a1408", background: "#faf7f0", padding: "3px 8px", borderRadius: 6 }}>{it.sc}</code></td>
                <td style={td}>{it.handler_key} · <span style={{ color: "#888" }}>{it.version}</span></td>
                <td style={td}>
                  {Object.entries(it.metadata || {}).map(([k, v]) => (
                    <div key={k} style={{ fontSize: 11, color: "#666" }}><strong>{k}:</strong> {String(v)}</div>
                  ))}
                </td>
                <td style={{ ...td, fontFamily: "ui-monospace,monospace", fontSize: 11 }}>{it.client_id || "—"}</td>
                <td style={td}>
                  {it.rate_limit}/min{it.signed && " · 🔏 signed"}
                </td>
                <td style={td}>
                  <button onClick={() => copy(it.sc)} style={btnSmall}>📋 Copy embed</button>{" "}
                  <a href={`${API}/api/s/${it.sc}`} target="_blank" rel="noopener noreferrer" style={{ ...btnSmall, textDecoration: "none" }}>👁 Preview</a>{" "}
                  <button onClick={() => retire(it.sc)} style={{ ...btnSmall, color: "#a64242" }}>🗑 Retire</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan="6" style={{ ...td, textAlign: "center", color: "#888", padding: 30 }}>No shortcodes minted yet — click "Mint shortcode" to start.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {probes && (probes.banned_ips.length > 0 || Object.keys(probes.probe_log_counts || {}).length > 0) && (
        <div style={{ marginTop: 26, background: "#fff5ed", border: "1px solid #f3c4a3", borderRadius: 10, padding: 18 }}>
          <div style={{ fontSize: 11, letterSpacing: 1.4, textTransform: "uppercase", color: "#a64423", fontWeight: 700, marginBottom: 8 }}>
            🚨 Canonical-URL probe activity
          </div>
          {probes.banned_ips.map((b) => (
            <div key={b.ip} style={{ fontSize: 12, color: "#1a1408", marginBottom: 4 }}>
              <strong>BANNED:</strong> <code>{b.ip}</code> · expires {new Date(b.expires_at * 1000).toLocaleString("en-AU", { timeZone: "Australia/Brisbane" })}
            </div>
          ))}
          {Object.entries(probes.probe_log_counts).map(([ip, n]) => (
            <div key={ip} style={{ fontSize: 12, color: "#666" }}>
              <code>{ip}</code> · {n} probe{n === 1 ? "" : "s"} in last 10 minutes
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 30, background: "#f6f3ea", borderLeft: "4px solid #a64423", padding: "14px 18px", borderRadius: 8, fontSize: 12, color: "#555", lineHeight: 1.55 }}>
        <strong>🔒 Security model:</strong> Each shortcode is opaque (base62, 8 chars · 218 trillion combos).
        Hits to canonical paths (e.g. <code>/api/reservations</code>) from public IPs are logged; 5+ probes in 10 minutes triggers a 1-hour IP ban.
        Sensitive shortcodes (payment, admin) can be marked <em>signed</em> — they require an HMAC signature on every request, so even guessing the code isn't enough.
      </div>
    </div>
  );
}

const th = { textAlign: "left", padding: "12px 14px", background: "#fafaf6", fontSize: 11, letterSpacing: 1.2, textTransform: "uppercase", color: "#888", fontWeight: 600, borderBottom: "1px solid #efe9da" };
const td = { padding: "12px 14px", borderBottom: "1px solid #f3eedf", verticalAlign: "top" };
const lblStyle = { fontSize: 11, letterSpacing: 1.2, textTransform: "uppercase", color: "#888", fontWeight: 600, marginBottom: 4 };
const inputStyle = { width: "100%", padding: "10px 12px", border: "1px solid #d6cfc0", borderRadius: 8, fontSize: 13, background: "#fafaf6", fontFamily: "inherit", boxSizing: "border-box" };
const btnPrimary = { padding: "10px 22px", background: "#1a1408", color: "#f3c84b", border: "none", borderRadius: 999, fontWeight: 700, letterSpacing: 1.4, textTransform: "uppercase", fontSize: 12, cursor: "pointer" };
const btnGhost = { padding: "10px 22px", background: "transparent", color: "#666", border: "1px solid #d6cfc0", borderRadius: 999, fontWeight: 600, fontSize: 12, cursor: "pointer" };
const btnSmall = { padding: "5px 10px", background: "transparent", border: "1px solid #d6cfc0", borderRadius: 6, fontSize: 11, cursor: "pointer", color: "#555" };
